import React, { useState, useEffect, useRef, useCallback } from 'react';

function getFileType(filename) {
  if (!filename) return "other";
  const ext = filename.split('.').pop().toLowerCase();
  if (["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg"].includes(ext)) return "image";
  if (["txt", "csv", "log", "json", "md", "xml"].includes(ext)) return "text";
  if (["pdf"].includes(ext)) return "pdf";
  if (["doc", "docx", "xls", "xlsx", "ppt", "pptx"].includes(ext)) return "doc";
  return "other";
}

function getContentType(content, filename) {
  // If content is a blob URL, determine type based on filename
  if (typeof content === 'string' && content.startsWith('blob:')) {
    // For DOCX files that were converted to PDF, treat as PDF
    if (filename && filename.toLowerCase().endsWith('.docx')) {
      return "pdf";
    }
    // For other blob URLs, use filename-based detection
    return getFileType(filename);
  }
  
  // If content is a regular string (not a blob URL), it's text content
  if (typeof content === 'string') {
    return "text";
  }
  
  // Default to filename-based type detection
  return getFileType(filename);
}

const FilePreviewModal = ({ isOpen, onClose, fileUri, filename, fetchFileContent }) => {
  const [content, setContent] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastFetchedUri, setLastFetchedUri] = useState(null);
  const [wordWrap, setWordWrap] = useState(true);
  const abortControllerRef = useRef(null);
  
  const fileType = getFileType(filename);
  
  // Get the actual content type (may differ for converted files)
  const contentType = content ? getContentType(content, filename) : fileType;

  // Cleanup function to revoke blob URLs and abort requests
  const cleanup = useCallback(() => {
    if (content && typeof content === 'string' && content.startsWith('blob:')) {
      URL.revokeObjectURL(content);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, [content]);

  // Fetch content only when needed
  const loadContent = useCallback(async (uri) => {
    // Don't fetch if already loading or if this is the same URI we just fetched
    if (loading || uri === lastFetchedUri) {
      console.log('FilePreviewModal: Skipping duplicate request for', uri);
      return;
    }

    // Mark this URI as attempted immediately to avoid retry loops on failure.
    setLastFetchedUri(uri);

    // Cleanup previous content
    cleanup();

    setLoading(true);
    setError(null);
    setContent(null);

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    try {
      console.log('FilePreviewModal: Fetching content for:', uri);
      const fetchedContent = await fetchFileContent(uri);
      
      // Check if request was aborted
      if (abortControllerRef.current?.signal.aborted) {
        console.log('FilePreviewModal: Request was aborted');
        return;
      }

      setContent(fetchedContent);
      console.log('FilePreviewModal: Content loaded successfully');
    } catch (err) {
      if (abortControllerRef.current?.signal.aborted) {
        console.log('FilePreviewModal: Request was aborted');
        return;
      }
      console.error('FilePreviewModal: Error fetching content:', err);
      setError(err.message || 'Failed to load file content');
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  }, [loading, lastFetchedUri, cleanup, fetchFileContent]);

  // Effect to load content when modal opens or URI changes
  useEffect(() => {
    if (isOpen && fileUri && fileUri !== lastFetchedUri) {
      loadContent(fileUri);
    }
  }, [isOpen, fileUri, lastFetchedUri, loadContent]);

  // Effect to cleanup when modal closes
  useEffect(() => {
    if (!isOpen) {
      cleanup();
      setContent(null);
      setError(null);
      setLastFetchedUri(null);
    }
  }, [isOpen, cleanup]);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const handleClose = () => {
    // Clean up blob URLs when closing
    cleanup();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal show d-block"
      tabIndex="-1"
      style={{
        background: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        inset: 0,
        zIndex: 2050,
      }}
    >
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{filename}</h5>
            <div className="ms-auto d-flex align-items-center">
              {contentType === 'text' && !loading && !error && content && (
                <div className="form-check form-switch me-3">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    id="wordWrapToggle"
                    checked={wordWrap}
                    onChange={(e) => setWordWrap(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="wordWrapToggle">
                    Word wrap
                  </label>
                </div>
              )}
              <button type="button" className="btn-close" onClick={handleClose}></button>
            </div>
          </div>
          <div className="modal-body" style={{ minHeight: 300 }}>
            {loading && <div>Loading...</div>}
            {error && <div className="text-danger">{error}</div>}
            {!loading && !error && content && (
              <>
                {contentType === 'image' && (
                  <img src={content} alt={filename} style={{ maxWidth: '100%' }} />
                )}
                {contentType === 'text' && (
                  (() => {
                    const isHtml = typeof content === 'string' && /<html|<!DOCTYPE html/i.test(content);
                    if (isHtml) {
                      return <div className="text-danger">Unable to display file content (HTML received instead of text file).</div>;
                    }
                    return (
                      <pre 
                        style={{ 
                          maxHeight: 500, 
                          overflow: 'auto',
                          whiteSpace: wordWrap ? 'pre-wrap' : 'pre',
                          wordBreak: wordWrap ? 'break-word' : 'normal'
                        }}
                      >
                        {content}
                      </pre>
                    );
                  })()
                )}
                {contentType === 'pdf' && (
                  <div>
                    {filename && filename.toLowerCase().endsWith('.docx') && (
                      <div className="alert alert-info mb-3">
                        <i className="bi bi-info-circle me-2"></i>
                        This DOCX file has been converted to PDF for preview.
                      </div>
                    )}
                    <iframe 
                      src={content} 
                      title={filename} 
                      style={{ width: '100%', height: 600, border: 'none' }}
                      onLoad={() => console.log('PDF iframe loaded successfully')}
                      onError={(e) => {
                        console.error('PDF iframe error:', e);
                        setError('Failed to load PDF preview');
                      }}
                    />
                    <div className="mt-2 text-center">
                      <a 
                        href={content} 
                        download={filename} 
                        className="btn btn-sm btn-outline-primary me-2"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <i className="bi bi-download me-1"></i>
                        Download {filename && filename.toLowerCase().endsWith('.docx') ? 'as PDF' : 'PDF'}
                      </a>
                      <a 
                        href={content} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-secondary"
                      >
                        <i className="bi bi-box-arrow-up-right me-1"></i>
                        Open in New Tab
                      </a>
                    </div>
                  </div>
                )}
                {contentType === 'doc' && fileType === 'doc' && !filename.toLowerCase().endsWith('.docx') && (
                  <iframe src={content} title={filename} style={{ width: '100%', height: 600 }} />
                )}
                {contentType === 'other' && (
                  <a href={content} download={filename} className="btn btn-primary">Download</a>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilePreviewModal;
